import { useEffect, useState } from 'react';
import { Card, Skeleton } from 'antd';
import { ReactComponent as TotalCountIcon } from '../../assets/images/total-count-icon.svg';
import { ReactComponent as NotMappedIcon } from '../../assets/images/not-mapped-icon.svg';
import { ReactComponent as MappedIcon } from '../../assets/images/mapped-icon.svg';
import {
	PreworkDashboardDetail,
	PreworkDashboardFilter,
} from '../../shared/PreWorkServiceTypes';
import { useTranslation } from 'react-i18next';
import { TOTAL_NO_OF_NPS_CARDS } from '../../shared/Constants';

const PreworkDetailCard = ({
	icon,
	candidateCount,
	candidateText,
	onClick,
	isSelected,
	isHovered,
	onMouseEnter,
	onMouseLeave,
}: {
	icon: JSX.Element;
	candidateCount?: number;
	candidateText: string;
	onClick?: () => void;
	isSelected?: boolean;
	isHovered?: boolean;
	onMouseEnter?: () => void;
	onMouseLeave?: () => void;
}) => {
	return (
		<div className={`w-full md:w-1/5 p-2`}>
			<Card
				onClick={onClick}
				onMouseEnter={onMouseEnter}
				onMouseLeave={onMouseLeave}
				className={`flex justify-around items-center border-2 ${
					isSelected || isHovered ? 'border-nav-blue' : 'border-gray-300'
				} w-full h-full rounded-none transition-colors duration-300 cursor-pointer`}
			>
				<div className="flex w-full gap-5 items-center min-h-32">
					<div className="flex gap-5 min-w-16">
						<div className="flex flex-col items-center justify-center">
							<div className="mb-2">{icon}</div>
							<h1 className="text-gray my-1">{candidateCount || 0}</h1>
						</div>
					</div>
					<p className="text-lg text-gray font-medium my-1 text-start">
						{candidateText}
					</p>
				</div>
			</Card>
		</div>
	);
};

const PreworkLoadingCard = () => {
	return (
		<>
			<Card className="w-1/5 rounded-none border-2 border-gray-300 mr-4">
				<Skeleton loading avatar active></Skeleton>
			</Card>
		</>
	);
};

export const PreworkDetailCardsList = ({
	dashboardDetail,
	onCardClick,
}: {
	dashboardDetail?: PreworkDashboardDetail;
	onCardClick?: (
		candidateDashboardCardFilter: PreworkDashboardFilter,
		cardIndex: number
	) => void;
}) => {
	const { t } = useTranslation();
	const [selectedCardIndex, setSelectedCardIndex] = useState<number | null>(0);
	const [hoveredCardIndex, setHoveredCardIndex] = useState<number | null>(null);

	useEffect(() => {
		setSelectedCardIndex(0);
	}, [dashboardDetail]);

	const handleCardClick = (index: number) => {
		if (!onCardClick) {
			return;
		}
		setSelectedCardIndex(index);
		const candidateDashboardCardFilter = {
			candidateMappedToGroup: false,
			candidateAssignedToMentor: false,
			candidateNotMappedToGroup: false,
			candidateNotAssignedToMentor: false,
		};

		switch (index) {
			case 0:
				onCardClick(candidateDashboardCardFilter, index);
				return;
			case 1:
				candidateDashboardCardFilter.candidateNotMappedToGroup = true;
				onCardClick(candidateDashboardCardFilter, index);
				return;
			case 2:
				candidateDashboardCardFilter.candidateMappedToGroup = true;
				onCardClick(candidateDashboardCardFilter, index);
				return;
			case 3:
				candidateDashboardCardFilter.candidateNotAssignedToMentor = true;
				onCardClick(candidateDashboardCardFilter, index);
				return;
			default:
				candidateDashboardCardFilter.candidateAssignedToMentor = true;
				onCardClick(candidateDashboardCardFilter, index);
				return;
		}
	};

	const handleMouseEnter = (index: number) => {
		setHoveredCardIndex(index);
	};

	const handleMouseLeave = () => {
		setHoveredCardIndex(null);
	};

	const getIconByIndex = (index: number) => {
		if (index === 0) return <TotalCountIcon className="w-12 h-12" />;
		if (index === 1) return <NotMappedIcon className="w-12 h-12" />;
		return index % 2 === 0 ? (
			<MappedIcon className="w-12 h-12" />
		) : (
			<NotMappedIcon className="w-12 h-12" />
		);
	};

	const getCandidateCountByIndex = (
		index: number,
		dashboardDetailParam?: PreworkDashboardDetail
	) => {
		switch (index) {
			case 0:
				return dashboardDetailParam?.totalCandidates;
			case 1:
				return dashboardDetailParam?.totalCandidatesNotMappedWithGroup;
			case 2:
				return dashboardDetailParam?.totalCandidatesMappedWithGroup;
			case 3:
				return dashboardDetailParam?.totalCandidatesNotAssignedWithMentor;
			default:
				return dashboardDetailParam?.totalCandidatesAssignedWithMentor;
		}
	};

	const getCandidateTextByIndex = (index: number) => {
		switch (index) {
			case 0:
				return t('totalCandidateCount');
			case 1:
				return t('candidatesNotMappedToGroup');
			case 2:
				return t('candidatesMappedToGroup');
			case 3:
				return t('candidatesNotMappedWithMentor');
			default:
				return t('candidatesMappedWithMentor');
		}
	};

	return (
		<div className="flex justify-between my-5">
			{(dashboardDetail
				? Array.from({ length: TOTAL_NO_OF_NPS_CARDS })
				: []
			).map((_, index) => (
				<PreworkDetailCard
					key={index}
					icon={getIconByIndex(index)}
					candidateCount={getCandidateCountByIndex(index, dashboardDetail)}
					candidateText={getCandidateTextByIndex(index)}
					onClick={onCardClick ? () => handleCardClick(index) : undefined}
					isSelected={onCardClick ? selectedCardIndex === index : undefined}
					isHovered={onCardClick ? hoveredCardIndex === index : undefined}
					onMouseEnter={onCardClick ? () => handleMouseEnter(index) : undefined}
					onMouseLeave={onCardClick ? handleMouseLeave : undefined}
				/>
			))}
			{!dashboardDetail &&
				Array.from({ length: TOTAL_NO_OF_NPS_CARDS }).map((_, index) => (
					<PreworkLoadingCard key={index} />
				))}
		</div>
	);
};
